import React, { useState, useEffect, useCallback } from 'react';
import Heading from '../../components/Heading';
import { useGlobalContext } from '../../contexts/GlobalContext';
import { useContactFormManagement } from '../../contexts/ContactFormManagementContext';
import { useShopUrl } from '../../hooks/useShopUrl';
import { useFieldOperations } from '../../hooks/useFieldOperations';
import { useFormSave } from '../../hooks/useFormSave';
import { FormHeader } from './Components/FormHeader';
import { TabNavigation } from './Components/TabNavigation';
import { TabName, getTabComponents } from './Components/tabConfig';
import DeleteFormModal from '../ContactFormOverview/DeleteFormModal';
import EditFormNameModal from '../ContactFormOverview/EditFormNameModal';
import ApiServiceV2 from '../../services/ApiServiceV2';
import { routes } from '../../hooks/routes';

const ContactForm: React.FC = () => {
    const { showToast, useRoute: { changeRoute }, showModal, hideModal } = useGlobalContext();
    const {
        currentForm,
        config,
        isLoading,
        updateConfig,
        saveCurrentForm,
        toggleFormVisibility,
        selectForm,
        clearSelection
    } = useContactFormManagement();

    const [activeTab, setActiveTab] = useState<TabName>('Felder');
    const shopUrl = useShopUrl();
    const [shopUrlState, setShopUrlState] = useState(shopUrl);

    const { updateField, addField, addGenericField, removeField, moveField } = useFieldOperations({ config, updateConfig });
    const { saveAllSettings } = useFormSave({ currentForm, config, saveCurrentForm, showToast });

    useEffect(() => {
        setShopUrlState(shopUrl);
    }, [shopUrl]);

    const handleDeleteForm = useCallback(async (formId: string) => {
        try {
            const api = new ApiServiceV2();
            await api.callApi(routes.deleteForm.replace(':id', formId), 'delete');
            
            showToast('top-right', 'success', 'Formular erfolgreich gelöscht!');
            hideModal();
            clearSelection();
            changeRoute('overview');
        } catch (error) {
            console.error('Failed to delete form:', error);
            showToast('top-right', 'error', 'Fehler beim Löschen des Formulars');
        }
    }, [showToast, hideModal, clearSelection, changeRoute]);

    const handleEditFormName = useCallback(async (formId: string, newName: string) => {
        try {
            const api = new ApiServiceV2();
            const response = await api.callApi(routes.updateForm.replace(':id', formId), 'post', { 
                name: newName 
            });
            
            if (response.data?.data?.success !== false) {
                showToast('top-right', 'success', 'Formularname erfolgreich geändert!');
                hideModal();
                // Reload the form to get an updated name
                if (formId) {
                    await selectForm(formId);
                }
            }
        } catch (error) {
            console.error('Failed to update form name:', error);
            showToast('top-right', 'error', 'Fehler beim Ändern des Formularnamens');
        }
    }, [showToast, hideModal, selectForm]);

    const openDeleteModal = useCallback(() => {
        if (!currentForm?.id || !currentForm?.name) return;
        
        showModal({
            content: (
                <DeleteFormModal
                    formId={currentForm.id}
                    formName={currentForm.name}
                    onConfirm={handleDeleteForm}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    }, [currentForm, showModal, handleDeleteForm, hideModal]);

    const openEditModal = useCallback(() => {
        if (!currentForm?.id || !currentForm?.name) return;
        
        showModal({
            content: (
                <EditFormNameModal
                    formId={currentForm.id}
                    currentName={currentForm.name}
                    onConfirm={handleEditFormName}
                    onCancel={hideModal}
                />
            ),
            fullscreen: false,
            size: 'sm'
        });
    }, [currentForm, showModal, handleEditFormName, hideModal]);

    if (!currentForm && !isLoading) {
        return (
            <div>
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    <Heading level={3} classes="no-form-selected">
                        Kein Formular ausgewählt
                    </Heading>
                    <p style={{ margin: '1rem 0 0 0' }}>
                        Bitte wählen Sie ein Formular aus der Übersicht aus, um es zu bearbeiten.
                    </p>
                </div>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div>
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    <div>Formular wird geladen...</div>
                </div>
            </div>
        );
    }

    if (!currentForm) {
        return (
            <div>
                <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
                    <Heading level={3} classes="no-form-selected">
                        Kein Formular ausgewählt
                    </Heading>
                    <p style={{ margin: '1rem 0 0 0' }}>
                        Bitte wählen Sie ein Formular aus der Übersicht aus, um es zu bearbeiten.
                    </p>
                </div>
            </div>
        );
    }

    const tabComponents = getTabComponents({
        config,
        updateConfig,
        updateField,
        addField,
        addGenericField,
        removeField,
        moveField,
        showModal,
        hideModal,
        showToast,
        saveAllSettings,
        shopUrl: shopUrlState,
        setShopUrl: setShopUrlState
    });

    return (
        <div className="zr-my-4">
            <FormHeader
                currentForm={currentForm}
                toggleFormVisibility={toggleFormVisibility}
                onBackClick={() => changeRoute('overview')}
                onEditName={openEditModal}
                onDelete={openDeleteModal}
            />
            <TabNavigation
                activeTab={activeTab}
                onTabChange={setActiveTab}
                onSave={saveAllSettings}
            />
            <div className={'zr-px-3'}>
                {tabComponents[activeTab]}
            </div>
        </div>
    );
};

export default ContactForm; 